import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IDeviceSession } from '../../Interfaces/IDeviceSession';
import { customFetcher } from '../useFetch';

const DEVICE_SESSIONS_KEY = ['sessions', 'admin'];

/**
 * Alle registrierten Geräte/Apps über alle Mitarbeiter hinweg (nur Admin).
 * `client` filtert serverseitig auf einen Client, z. B. `mitarbeiter-app`.
 */
export const useGetAllDeviceSessions = (client?: string) => {
  const {
    data: deviceSessions,
    isLoading: isLoadingDeviceSessions,
    refetch: refetchDeviceSessions,
  } = useQuery<IDeviceSession[]>({
    queryKey: [...DEVICE_SESSIONS_KEY, client ?? 'all'],
    queryFn: () =>
      customFetcher({
        endpoint: client
          ? `/sessions/admin/list?client=${encodeURIComponent(client)}`
          : '/sessions/admin/list',
      }),
  });
  return { deviceSessions, isLoadingDeviceSessions, refetchDeviceSessions };
};

/** Ein einzelnes Gerät abmelden. */
export const useRevokeDeviceSession = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: revokeDeviceSession,
    isPending: isPendingRevokeDeviceSession,
  } = useMutation({
    mutationFn: (idUserSession: number) =>
      customFetcher({
        endpoint: `/sessions/admin/${idUserSession}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_SESSIONS_KEY });
    },
  });
  return { revokeDeviceSession, isPendingRevokeDeviceSession };
};

/**
 * Sämtliche Geräte eines Mitarbeiters abmelden — der Fall „Mitarbeiter
 * scheidet aus". Ohne `client` werden alle Zugänge entzogen (Web, Smartphone,
 * Plugins).
 */
export const useRevokeAllDeviceSessionsOfUser = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: revokeAllDeviceSessionsOfUser,
    isPending: isPendingRevokeAllOfUser,
  } = useMutation({
    mutationFn: ({ idUser, client }: { idUser: number; client?: string }) =>
      customFetcher({
        endpoint: client
          ? `/sessions/admin/user/${idUser}?client=${encodeURIComponent(client)}`
          : `/sessions/admin/user/${idUser}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_SESSIONS_KEY });
    },
  });
  return { revokeAllDeviceSessionsOfUser, isPendingRevokeAllOfUser };
};
